/**
 * Clinics map: Google Maps JS API when data-api-key is set,
 * otherwise Leaflet + OpenStreetMap (no key).
 */
(function () {
  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  var LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  var gmapsPromise = null;

  function parseLocations(root) {
    var node = root.querySelector('[data-cb-map-locations]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent || '[]').filter(function (loc) {
        return loc && isFinite(Number(loc.lat)) && isFinite(Number(loc.lng));
      });
    } catch (e) {
      return [];
    }
  }

  function loadStylesheet(href) {
    if (document.querySelector('link[href="' + href + '"]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === '1') return resolve();
        existing.addEventListener('load', function () {
          resolve();
        });
        existing.addEventListener('error', reject);
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = function () {
        script.dataset.loaded = '1';
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) return Promise.resolve();
    if (gmapsPromise) return gmapsPromise;
    gmapsPromise = new Promise(function (resolve, reject) {
      var cbName = '__cbGmapsReady_' + String(Date.now());
      window[cbName] = function () {
        try {
          delete window[cbName];
        } catch (e) {}
        resolve();
      };
      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&callback=' +
        cbName;
      script.async = true;
      script.defer = true;
      script.onerror = function () {
        reject(new Error('Google Maps failed to load'));
      };
      document.head.appendChild(script);
    });
    return gmapsPromise;
  }

  function popupHtml(loc) {
    var address = (loc.address || '').replace(/\n/g, '<br>');
    var html = '<div class="cb-map-popup"><strong>' + escapeHtml(loc.title || 'Clinic') + '</strong>';
    if (address) html += '<div>' + address + '</div>';
    if (loc.url) {
      html +=
        '<a href="' +
        escapeAttr(loc.url) +
        '" target="_blank" rel="noopener">Open in Google Maps</a>';
    }
    html += '</div>';
    return html;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/'/g, '&#39;');
  }

  function initGoogle(root, locations, markerColor) {
    var mapEl = document.createElement('div');
    mapEl.className = 'cb-gmap';
    root.appendChild(mapEl);

    var map = new google.maps.Map(mapEl, {
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    });

    var bounds = new google.maps.LatLngBounds();
    var info = new google.maps.InfoWindow();

    locations.forEach(function (loc) {
      var position = { lat: Number(loc.lat), lng: Number(loc.lng) };
      bounds.extend(position);
      var marker = new google.maps.Marker({
        map: map,
        position: position,
        title: loc.title || '',
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: markerColor,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
      marker.addListener('click', function () {
        info.setContent(popupHtml(loc));
        info.open({ map: map, anchor: marker });
      });
    });

    if (locations.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(14);
    } else {
      map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
    }
  }

  function initLeaflet(root, locations, markerColor) {
    loadStylesheet(LEAFLET_CSS);
    return loadScript(LEAFLET_JS).then(function () {
      var map = L.map(root, {
        scrollWheelZoom: false,
        attributionControl: true,
      });

      if (map.attributionControl) {
        map.attributionControl.setPrefix('');
      }

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);

      var bounds = [];
      locations.forEach(function (loc) {
        var latlng = [Number(loc.lat), Number(loc.lng)];
        bounds.push(latlng);
        var icon = L.divIcon({
          className: '',
          html:
            '<div class="cb-map-pin" style="background:' +
            escapeAttr(markerColor) +
            '"></div>',
          iconSize: [28, 28],
          iconAnchor: [14, 28],
          popupAnchor: [0, -24],
        });
        L.marker(latlng, { icon: icon }).addTo(map).bindPopup(popupHtml(loc));
      });

      if (bounds.length === 1) {
        map.setView(bounds[0], 14);
      } else {
        map.fitBounds(bounds, { padding: [40, 40] });
      }

      // Leaflet needs a resize after layout settles in Shopify sections
      setTimeout(function () {
        map.invalidateSize();
      }, 100);
    });
  }

  function initRoot(root) {
    if (root.dataset.cbMapReady === '1') return;
    var locations = parseLocations(root);
    if (!locations.length) return;

    root.dataset.cbMapReady = '1';
    var apiKey = (root.getAttribute('data-api-key') || '').trim();
    var markerColor = root.getAttribute('data-marker-color') || '#7F368A';

    // Remove JSON + noscript from flow once we render the map
    var jsonNode = root.querySelector('[data-cb-map-locations]');
    if (jsonNode) jsonNode.remove();

    if (apiKey) {
      loadGoogleMaps(apiKey)
        .then(function () {
          initGoogle(root, locations, markerColor);
        })
        .catch(function () {
          root.dataset.cbMapReady = '0';
          initLeaflet(root, locations, markerColor);
        });
    } else {
      initLeaflet(root, locations, markerColor);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-cb-clinics-map]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    e.target.querySelectorAll('[data-cb-clinics-map]').forEach(function (el) {
      el.dataset.cbMapReady = '0';
      initRoot(el);
    });
  });
})();
